// src/components/ProfitPanel.tsx (2026-07-01 新設)
// 粗利分析タブ。最上部に科別 群分類の100%積み上げ横棒(上=延数/下=粗利・全科・絞り込み非連動)、
//   その下に 科別→医師別→DPC別 の3表を縦並べ(各トグル開閉)。
//   科別クリック→focusedDept で医師別/DPC別を絞り込み、医師別クリック→focusedDoctor でDPC別を絞り込み。
//   [絞り込み保持 2026-07-17] DPC別は DPC分析タブと同じデザインに統一:
//   階層タブ(DpcTopBar=疾患別/手術別/処置1別/処置2別/完全) + 行クリックで絞り込み
//   (dpc6/selectedDpc10-12 をサイドバーのチップと共有・階層切替でも保持)。
import { useState, useMemo, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { authApi } from "../lib/auth-api";
import { useFilters, applyDpcRowSelection, effectiveDpcSelection, selectedDpcAt, PROFIT_BASIS_DISCHARGE_ENABLED } from "../store";
import { useProfitPeriod } from "../lib/useProfitPeriod";
import { useDpcLabels } from "../lib/useDpcLabels";
import { ProfitTable } from "./ProfitTable";
import { DpcTopBar } from "./DpcTopBar";
import type { ProfitResponse } from "@shared/api";
import { gpText } from "../lib/gpMode";
import { fiscalYearOf, selectedMonthDates } from "../lib/fiscalYear";
import { GP_TAB_CARD_SPECS, resolveMetric, type ResolvedMetric } from "./prototypes/budgetSpec";
import { CompareCards, periodValue, meanSelected, type TrendOf } from "./InpatientBudgetComparePanel";
import type { DeptMetricsAllResponse } from "@shared/api";

const GUN_COLORS: Record<string, string> = { "1群": "#1B9E77", "2群": "#386FA4", "3群": "#D9A036", "4群": "#94a3b8" };

// ★2026-08-22: null (= 1日粗利単価が算出できない) を "4群" に落とさない。
//   GP が無い期間を選んだときに全科が「4群 (最下位)」に見えるのを防ぐ。呼び出し側で除外する。
function gunOf(gpT: number | null, g1: number, g2: number, g3: number): string | null {
  return gpT == null ? null : gpT >= g1 ? "1群" : gpT >= g2 ? "2群" : gpT >= g3 ? "3群" : "4群";
}

// 100% 積み上げ横棒(1本)。棒内に 群名+構成比% と実績テキスト。
// [2026-07-18 ユーザ指示] バー内テキストに % を付け、下に % 目盛 (StackBarAxis) を出す。
function StackBar({ label, segs, fmt }: { label: string; segs: { g: string; v: number }[]; fmt: (v: number) => string }) {
  const total = segs.reduce((a, s) => a + s.v, 0) || 1;
  return (
    <div className="profit-stack-row flex items-center gap-2 mb-1.5">
      <div className="profit-stack-label text-xs text-slate-600 w-20 shrink-0 text-right">{label}</div>
      <div className="profit-stack-bar flex w-full h-9 rounded overflow-hidden border border-slate-200">
        {segs.filter((s) => s.v > 0).map((s) => {
          const pct = (s.v / total) * 100;
          return (
            <div key={s.g} style={{ width: `${pct}%`, background: GUN_COLORS[s.g] }}
              className="flex flex-col items-center justify-center text-white overflow-hidden whitespace-nowrap"
              title={`${s.g}: ${fmt(s.v)} (${pct.toFixed(1)}%)`}>
              {pct >= 7 && (<>
                <span className="text-[10px] font-semibold leading-tight">{s.g} {pct.toFixed(0)}%</span>
                <span className="text-[10px] leading-tight">{fmt(s.v)}</span>
              </>)}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// % 目盛の x 軸 (0/25/50/75/100%・StackBar 2 本の下に 1 本だけ描く)
function StackBarAxis() {
  return (
    <div className="profit-stack-axis flex items-center gap-2">
      <div className="w-20 shrink-0" />
      <div className="relative w-full h-4">
        {[0, 25, 50, 75, 100].map((p) => (
          <span key={p}
            className="absolute text-[9px] text-slate-400 -translate-x-1/2"
            style={{ left: `${p}%` }}>{p}%</span>
        ))}
      </div>
    </div>
  );
}

function Section({ title, sub, children, defaultOpen = true, className = "", forcePrintOpen = false }: {
  title: string;
  sub?: string;
  children: ReactNode;
  defaultOpen?: boolean;
  className?: string;
  forcePrintOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className={`card p-3 mb-3 ${className}`}>
      <button onClick={() => setOpen(!open)} className="flex items-center gap-2 w-full text-left mb-1">
        <span className="section-toggle-icon text-slate-400 text-xs">{open ? "▼" : "▶"}</span>
        <h3 className="text-sm font-bold">{title}</h3>
        {sub && <span className="text-[11px] text-slate-500">{sub}</span>}
      </button>
      {open ? children : forcePrintOpen ? <div className="hidden print:block">{children}</div> : null}
    </section>
  );
}

export function ProfitPanel({ showDoctor = true }: { showDoctor?: boolean }) {
  const f = useFilters();
  const vol = f.kpiVolumeBase === "在院" ? "在院" : "入院";

  const isDischarge = f.profitBasis === "退院";
  // 医師別は主治医情報。can_view_doctor (admin/責任者) のみ表示 (server も 403 で保護)。
  const { data: me } = useQuery({ queryKey: ["auth", "me"], queryFn: authApi.me, retry: false, staleTime: 30_000 });
  const canViewDoctor = me?.user.can_view_doctor ?? false;

  // [2026-07-17] 期間は粗利専用 (default=最新確定月のみ)・meta はキャッシュ共有
  const period = useProfitPeriod();
  const { data: meta } = useQuery({ queryKey: ["meta"], queryFn: api.meta, staleTime: Infinity });

  // ── 先頭カード (2026-09-10 ユーザ指示) ────────────────────────────────
  // GP の作り方をそのまま 4 枚並べる: 入院収入 → 薬剤材料費(出来高換算額) → 医師人件費 → GP。
  // 入院実績タブと同じ CompareCards をそのまま使う (見た目・予算比/前年比・ミニトレンドを共通化)。
  // 期間は GP分析タブの期間 (useProfitPeriod) に連動させ、科フォーカスにも追従する。
  const cardFy = fiscalYearOf(meta?.dateMax ?? period.to);
  const cardFyStart = `${cardFy}-04-01`;
  const cardFyEnd = `${cardFy + 1}-03-01`;
  const cardMonths = useMemo(
    () => selectedMonthDates(cardFy, period.from, period.to),
    [cardFy, period.from, period.to]);
  const { data: cardData } = useQuery<DeptMetricsAllResponse>({
    queryKey: ["gp_cards", f.granularity, f.profitIryo, f.yotei, cardFyEnd, cardFyStart, f.focusedDept, f.routeKeiyu],
    queryFn: () => api.deptMetricsAll({
      granularity: f.granularity, iryo: f.profitIryo, yotei: f.yotei,
      asOfMonth: cardFyEnd, periodFrom: cardFyStart,
      focusedDept: f.focusedDept ?? undefined, trendBack: 11, keiyu: f.routeKeiyu,
    }),
    placeholderData: (prev) => prev,
  });
  const { cardResolved, cardTrendOf } = useMemo(() => {
    if (!cardData) return { cardResolved: [] as ResolvedMetric[], cardTrendOf: (() => undefined) as TrendOf };
    const resolved = GP_TAB_CARD_SPECS.map((sp) => resolveMetric(sp, cardData));
    const byKey = new Map(resolved.map((r) => [r.spec.key, r] as const));
    const raw = new Map(cardData.rows.map((r) => [r.集計区分, r.trend] as const));
    return { cardResolved: resolved, cardTrendOf: ((m) => byKey.get(m)?.trend ?? raw.get(m)) as TrendOf };
  }, [cardData]);
  const cardPeriodCalc = (r: ResolvedMetric, field: "actual" | "budget" | "prevYear") =>
    periodValue(r.spec.key, field, cardMonths, cardTrendOf, () => meanSelected(r, cardMonths, field));

  // 科別データ(チャート用・常に全科)。期間基準(在院日/退院日)に連動。
  const { data: deptData } = useQuery<ProfitResponse>({
    queryKey: ["profit-chart", "dept", f.profitBasis, f.granularity, f.yotei, f.profitIryo, f.kpiVolumeBase, period.from, period.to, f.routeKeiyu],
    queryFn: () => api.profit({ axis: "dept", basis: f.profitBasis, granularity: f.granularity, yotei: f.yotei, iryo: f.profitIryo, volBase: f.kpiVolumeBase, dateFrom: period.from, dateTo: period.to, focusedDept: null, focusedDoctor: null, keiyu: f.routeKeiyu }),
    placeholderData: (p) => p,
  });
  const chart = useMemo(() => {
    const rows = (deptData?.rows ?? []).filter((r) => r.key !== "__total__");
    const acc: Record<string, { days: number; gp: number }> = { "1群": { days: 0, gp: 0 }, "2群": { days: 0, gp: 0 }, "3群": { days: 0, gp: 0 }, "4群": { days: 0, gp: 0 } };
    for (const r of rows) {
      const g = gunOf(r["1日粗利単価"], f.gun1, f.gun2, f.gun3);
      if (g == null || r.粗利 == null) continue;   // GP 未算出の期間は群分類に載せない
      acc[g].days += r.延数; acc[g].gp += r.粗利;
    }
    const order = ["1群", "2群", "3群", "4群"];
    return { days: order.map((g) => ({ g, v: acc[g].days })), gp: order.map((g) => ({ g, v: acc[g].gp })) };
  }, [deptData, f.gun1, f.gun2, f.gun3]);

  const lvl = f.dpcLevel;

  // [条件サマリ 2026-07-17] 適用中フィルタと期間・データ更新時刻を 1 行に集約。
  // 印刷時はサイドバー(aside)が消えるため、このバーが「いつの・どういう絞り込みのデータか」を担う。
  const ymShort = (d: string) => `${d.slice(2, 4)}年${d.slice(5, 7)}月`;
  const periodLabel = period.from === period.to ? ymShort(period.from) : `${ymShort(period.from)} 〜 ${ymShort(period.to)}`;
  const dpcSel = effectiveDpcSelection(f);
  const { data: dpcLabels } = useDpcLabels(f.selectedDpc10, f.selectedDpc11, f.selectedDpc12);
  const dpcSelName = dpcSel == null ? null
    : dpcSel.length >= 12 ? (dpcLabels?.name12 ?? dpcSel)
    : dpcSel.length === 11 ? (dpcLabels?.name11 ?? dpcSel)
    : (dpcLabels?.name10 ?? dpcSel);
  const builtAt = meta?.builtAt
    ? new Date(meta.builtAt).toLocaleString("ja-JP", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" })
    : null;
  const pill = "inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[11px]";

  return (
    <div className="profit-print-root">
      {/* 印刷 page 1 は 条件ヘッダ + 群分類構成 + 科別表 を一体化する。
          個別 card の break-inside:avoid で科別表だけ次ページへ送られないよう、専用 CSS で高さを調整する。 */}
      <div className="profit-print-page-1">
      {/* [条件サマリ 2026-07-17] タイトル + 適用条件バー (印刷にもそのまま載る) */}
      <section className="profit-print-header card px-4 py-2.5 mb-3">
        <div className="flex items-center gap-1.5 flex-wrap">
          <h2 className="profit-print-title text-base font-bold mr-1">💹 {gpText("粗利分析")}{showDoctor && <span className="text-xs font-normal text-slate-400 ml-1">(医師別あり)</span>}</h2>
          {/* ★2026-08-27: 退院日基準を封鎖中は選択肢が 在院日 しか無いので「在院日基準」pill も出さない
              (選べないものを条件として掲げない)。フラグを戻せば pill ごと復活する。 */}
          {PROFIT_BASIS_DISCHARGE_ENABLED && (
            <span className={`${pill} ${isDischarge ? "bg-violet-50 border-violet-200 text-violet-800" : "bg-teal-50 border-teal-200 text-teal-800"} font-semibold`}>
              {f.profitBasis}日基準
            </span>
          )}
          <span className={`${pill} bg-slate-50 border-slate-200 text-slate-700 font-semibold`}>
            📅 {periodLabel}{period.isLatestOnly && <span className="text-[9px] text-slate-400 font-normal">最新月</span>}
          </span>
          <span className={`${pill} border-slate-200 ${(!isDischarge && f.profitIryo !== "急性期") ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-white text-slate-600"}`}>
            医療区分: {isDischarge ? "急性期" : f.profitIryo}
          </span>
          <span className={`${pill} border-slate-200 ${f.yotei !== "合計" ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-white text-slate-600"}`}>
            予定緊急: {f.yotei}
          </span>
          {/* [2026-08-26 ユーザ指示] 経由フィルタ中は印刷にも絞り込みが分かるように出す。
              サイドバーの grainOk 判定 (isRoute || profitDischarge || 粒度=小) と同じ条件でのみ表示:
              在院基準×粒度中/大 は経由フィルタが実際には効かない (fact_long_keiyu が小分類限定)
              ため、そのケースでは f.routeKeiyu が "合計" 以外でもここには出さない。 */}
          {(isDischarge || f.granularity === "小") && f.routeKeiyu !== "合計" && (
            <span className={`${pill} bg-amber-50 border-amber-200 text-amber-800 font-semibold`}>
              🚪 経由: {f.routeKeiyu}
            </span>
          )}
          <span className={`${pill} bg-white border-slate-200 text-slate-600`}>{f.granularity}分類</span>
          <span className={`${pill} border-slate-200 ${f.focusedDept ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-white text-slate-600"}`}>
            {f.focusedDept ?? "全科"}
          </span>
          {isDischarge && f.dpc6 && (
            <span className={`${pill} bg-emerald-50 border-emerald-200 text-emerald-800`}>
              DPC: {f.dpc6}{dpcSelName && dpcSel !== f.dpc6 ? ` ${dpcSelName}` : ""}
            </span>
          )}
          <span className={`${pill} bg-white border-slate-200 text-slate-600`}>{vol}単価</span>
          {builtAt && <span className="ml-auto text-[10px] text-slate-400">データ更新: {builtAt}</span>}
        </div>
      </section>

      {/* [2026-09-10 ユーザ指示] ページ最上部に GP の作り方を 4 枚のカードで並べる。
          入院収入 −(薬剤材料費) −(医師人件費) = GP。入院実績タブと同じ CompareCards を
          そのまま使うので、予算比/前年比の色分け・ミニトレンド・億⇄万の自動判定も同じになる。
          期間・医療区分・予定緊急・経由・科フォーカスは GP分析タブの条件に連動する。 */}
      {cardResolved.length > 0 && (
        <div className="profit-print-cards mb-3">
          <CompareCards resolved={cardResolved} fy={cardFy} periodCalc={cardPeriodCalc} />
        </div>
      )}

      {/* 科別 群分類 100%積み上げ横棒(絞り込み非連動・全科) */}
      <section className="profit-print-chart card p-3 mb-3">
        <h2 className="text-sm font-bold mb-2">📊 {gpText("科別 群分類 構成（1日粗利単価で4分類・全科）")}</h2>
        <StackBar label={`${vol}延数`} segs={chart.days} fmt={(v) => Math.round(v).toLocaleString() + "人日"} />
        <StackBar label={gpText("粗利")} segs={chart.gp} fmt={(v) => Math.round(v / 1000).toLocaleString() + "千円"} />
        <StackBarAxis />
        {/* [2026-07-18 ユーザ指示] 凡例は閾値レンジ表記 (1群(10万円以上) / 2群(8万円〜10万円) …) */}
        {(() => {
          // 万円表記 (1000 円単位までは小数で: 115000 → 11.5万円)。端数はそのまま円表記。
          const thr = (v: number) => (v >= 10000 && v % 1000 === 0
            ? `${(v / 10000).toLocaleString("ja-JP", { maximumFractionDigits: 1 })}万円`
            : `${v.toLocaleString()}円`);
          const items = [
            { g: "1群", label: `1群 (${thr(f.gun1)}以上)` },
            { g: "2群", label: `2群 (${thr(f.gun2)}〜${thr(f.gun1)})` },
            { g: "3群", label: `3群 (${thr(f.gun3)}〜${thr(f.gun2)})` },
            { g: "4群", label: `4群 (${thr(f.gun3)}未満)` },
          ];
          return (
            <div className="profit-stack-legend flex gap-3 text-[10px] text-slate-500 mt-1 flex-wrap" style={{ marginLeft: "5.5rem" }}>
              <span className="text-slate-400">{gpText("1日粗利単価")}:</span>
              {items.map((i) => (
                <span key={i.g} className="flex items-center gap-1">
                  <span style={{ width: 10, height: 10, background: GUN_COLORS[i.g], display: "inline-block", borderRadius: 2 }}></span>
                  {i.label}
                </span>
              ))}
            </div>
          );
        })()}
      </section>

      {/* [2026-07-23 印刷改善] 群分類構成と科別表を A4 横の同じ1ページ目へ固定する。 */}
      <Section className="profit-dept-section" forcePrintOpen title="科別" sub={isDischarge
        ? "行クリックで医師別/DPC別を絞り込み"
        : PROFIT_BASIS_DISCHARGE_ENABLED
          ? "在院日基準は科別のみ・行クリックで注目科をマーク"
          : "行クリックで注目科をマーク"}>
        <ProfitTable axis="dept" nameHeader="診療科" csvName={gpText("粗利分析_科別")}
          focusedKey={f.focusedDept}
          onRowClick={(r) => r.key !== "__total__" && f.setFocusedDept(f.focusedDept === r.key ? null : r.key)} />
      </Section>
      </div>

      {/* [2026-07-17 ユーザ指示] 絞り込みはサイドバーに凝縮。ここに出すのは
          サイドバーに解除導線が無い「医師」だけ (医師別表の行クリックで設定されるため)。 */}
      {isDischarge && f.focusedDoctor && (
        <div className="mb-2 text-xs flex items-center gap-2 flex-wrap">
          <span className="text-slate-500">絞り込み中:</span>
          <button onClick={() => f.setFocusedDoctor(null)} className="px-2 py-0.5 bg-amber-100 rounded hover:bg-amber-200">🎯 医師 ✕</button>
        </div>
      )}

      {isDischarge && showDoctor && canViewDoctor && (
        <Section title="医師別" sub={f.focusedDept ? `科=${f.focusedDept} で絞り込み中 · 行クリックでDPC別を絞り込み` : "行クリックでDPC別を絞り込み"}>
          <ProfitTable axis="doctor" nameHeader="医師" csvName={gpText("粗利分析_医師別")} focusedKey={f.focusedDoctor}
            onRowClick={(r) => f.setFocusedDoctor(f.focusedDoctor === r.key ? null : r.key)} />
        </Section>
      )}

      {isDischarge && (() => {
        // 表示階層での「選択中」コード (行ハイライト用)。DPC分析主表と同じ規則。
        const dpcFocusedKey = selectedDpcAt(f, lvl);
        // [中央値 2026-07-22 ユーザ指示] DPC別のみ 1日単価が中央値 (科別/医師別は加重平均のまま)
        return (
          <Section title="DPC別" sub="行クリックで絞り込み→次の階層へ (絞り込みはサイドバーに保持・DPC分析タブと共有) ※1日単価は患者1人ずつの単価の中央値">
            <div className="mb-2">
              <DpcTopBar />
            </div>
            <ProfitTable axis="dpc" dpcLevel={lvl} nameHeader="DPC / 疾患名" csvName={gpText("粗利分析_DPC別")}
              focusedKey={dpcFocusedKey}
              onRowClick={(r) => applyDpcRowSelection(r.key, lvl)} />
          </Section>
        );
      })()}
    </div>
  );
}
